import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faRobot } from '@fortawesome/free-solid-svg-icons';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ChatMessage } from '../types';
import TypewriterText from './TypewriterText';
import QueryLink from './QueryLink';
import HorizontalBarChart from './HorizontalBarChart';
import DocumentCard from './DocumentCard';
import SuggestionChips from './SuggestionChips';
import RatingWidget from './RatingWidget';
import type { MessageRatingValue } from '../types';

interface ChatBubbleProps {
  message: ChatMessage;
  /** If true, skip the typewriter animation and show content instantly */
  skipAnimation?: boolean;
  /** Called when a user clicks a bar in the facet chart */
  onFacetClick?: (label: string) => void;
  /** Called when user clicks a suggestion chip */
  onSuggestionClick?: (text: string) => void;
  /** Callback when query link is clicked */
  onQueryClick?: (url: string) => void;
  /** Show local debug diagnostics when enabled from block settings */
  showDebugInfo?: boolean;
  /** Enable the per-response rating control (feedback endpoint configured) */
  feedbackEnabled?: boolean;
  /** Rating UI style: 1-5 stars or thumbs up/down. */
  feedbackStyle?: 'stars' | 'thumbs';
  /** Tags shown for positive ratings (thumbs up / high stars). */
  feedbackTagsPositive?: string[];
  /** Tags shown for negative ratings (thumbs down / low stars). */
  feedbackTagsNegative?: string[];
  /** Submit a rating for this message. Should reject on failure. */
  onSubmitFeedback?: (
    messageId: string,
    value: MessageRatingValue,
    comment: string,
    tags: string[],
  ) => Promise<void>;
}

/**
 * Renders a single chat message bubble.
 * - User messages: right-aligned, slate styling.
 * - Assistant messages: left-aligned, amber/yellow theme with
 *   optional tool_data widgets (query link + bar chart + document + suggestions).
 */
const ChatBubble: React.FC<ChatBubbleProps> = ({
  message,
  skipAnimation = false,
  onFacetClick,
  onSuggestionClick,
  onQueryClick,
  showDebugInfo,
  feedbackEnabled = false,
  feedbackStyle = 'stars',
  feedbackTagsPositive = [],
  feedbackTagsNegative = [],
  onSubmitFeedback,
}) => {
  const isUser = message.role === 'user';
  const isStreaming = message.isStreaming ?? false;
  const [typewriterDone, setTypewriterDone] = useState(skipAnimation);
  const wasStreamingRef = useRef(isStreaming);
  const toolData = (message.tool_data || {}) as Record<string, unknown>;
  const responseTimeLabel =
    !isUser && typeof message.responseTimeMs === 'number'
      ? message.responseTimeMs >= 1000
        ? `${(message.responseTimeMs / 1000).toFixed(1)}s`
        : `${message.responseTimeMs}ms`
      : '';

  const generatedQueryUrl =
    (typeof toolData.generated_query_url === 'string' &&
      toolData.generated_query_url) ||
    (typeof toolData.generatedQueryUrl === 'string' &&
      toolData.generatedQueryUrl) ||
    '';

  const chartTitle =
    (typeof toolData.chart_title === 'string' && toolData.chart_title) ||
    (typeof toolData.chartTitle === 'string' && toolData.chartTitle) ||
    undefined;

  const getChartData = () => {
    const raw = toolData.chart_data ?? toolData.chartData;
    if (!raw) return [];

    let parsed: unknown = raw;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch {
        return [];
      }
    }

    if (Array.isArray(parsed)) {
      return parsed
        .map((item) => {
          const row = item as { label?: unknown; value?: unknown };
          if (typeof row.label !== 'string') return null;
          const value = Number(row.value);
          if (!Number.isFinite(value)) return null;
          return { label: row.label, value };
        })
        .filter(
          (item): item is { label: string; value: number } => item !== null,
        );
    }

    if (parsed && typeof parsed === 'object') {
      return Object.entries(parsed as Record<string, unknown>)
        .map(([label, value]) => {
          const n = Number(value);
          if (!Number.isFinite(n)) return null;
          return { label, value: n };
        })
        .filter(
          (item): item is { label: string; value: number } => item !== null,
        );
    }

    return [];
  };

  const chartData = getChartData();
  const debugToolKeys = Object.keys(toolData);
  const canRate =
    !isUser &&
    feedbackEnabled &&
    !isStreaming &&
    Boolean(message.content.trim()) &&
    !message.id.startsWith('error-') &&
    message.id !== 'retry-status';
  const isLocalDebugHost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.endsWith('.local'));

  useEffect(() => {
    if (isStreaming) {
      wasStreamingRef.current = true;
      return;
    }

    if (wasStreamingRef.current) {
      setTypewriterDone(true);
    }
  }, [isStreaming]);

  // During streaming or after animation, render as Markdown.
  // The typewriter is only for completed JSON responses that haven't been seen.
  const showMarkdown =
    isStreaming || skipAnimation || typewriterDone || wasStreamingRef.current;

  return (
    <div
      className={`ves-chat-bubble ${isUser ? 'ves-chat-bubble--user' : 'ves-chat-bubble--agent'}`}
    >
      {/* Avatar */}
      <div
        className={`ves-avatar ${isUser ? 'ves-avatar--user' : 'ves-avatar--agent'}`}
      >
        <FontAwesomeIcon icon={isUser ? faUser : faRobot} />
      </div>

      {/* Bubble */}
      <div className="ves-bubble-content">
        {/* Role label */}
        <div
          className={`ves-role-label ${isUser ? 'ves-role-label--user' : 'ves-role-label--agent'}`}
        >
          {isUser ? 'You' : 'Agent'}
        </div>

        {/* Message content — hide empty bubble when only tool_data is present */}
        {(isUser || message.content.trim()) && (
          <div
            className={`ves-message ${isUser ? 'ves-message--user' : 'ves-message--agent'}`}
          >
            {isUser ? (
              message.content
            ) : showMarkdown ? (
              <div className="ves-markdown-content">
                <Markdown remarkPlugins={[remarkGfm]}>
                  {message.content}
                </Markdown>
                {/* Blinking cursor while streaming */}
                {isStreaming && <span className="ves-streaming-cursor" />}
              </div>
            ) : (
              <TypewriterText
                text={message.content}
                speed={18}
                onComplete={() => setTypewriterDone(true)}
              />
            )}
          </div>
        )}

        {/* Show a streaming indicator when content is empty but streaming */}
        {!isUser && isStreaming && !message.content.trim() && (
          <div className="ves-message ves-message--agent ves-loading-dots">
            <span className="ves-dot" />
            <span className="ves-dot" />
            <span className="ves-dot" />
          </div>
        )}

        {responseTimeLabel && !isStreaming && (
          <div className="ves-response-time" title="Agent response time">
            Response time: {responseTimeLabel}
          </div>
        )}

        {/* Tool Data Widgets - show after typewriter/streaming finishes (or immediately if no text) */}
        {!isUser &&
          message.tool_data &&
          !isStreaming &&
          (typewriterDone || !message.content.trim()) && (
            <div className="ves-tool-data">
              {showDebugInfo && isLocalDebugHost && (
                <p className="ves-verification-debug" role="note">
                  Debug tool_data keys: {debugToolKeys.join(', ') || '(none)'} |
                  chart rows: {chartData.length}
                </p>
              )}
              {generatedQueryUrl && (
                <QueryLink
                  url={generatedQueryUrl}
                  onQueryClick={onQueryClick}
                />
              )}
              {chartData.length > 0 && (
                <HorizontalBarChart
                  data={chartData}
                  title={chartTitle}
                  onBarClick={
                    onFacetClick ? (dp) => onFacetClick(dp.label) : undefined
                  }
                />
              )}
              {message.tool_data.document_data && (
                <DocumentCard
                  title={message.tool_data.document_data.title}
                  pages={message.tool_data.document_data.pages}
                  url={message.tool_data.document_data.url}
                />
              )}
              {message.tool_data.suggestions &&
                message.tool_data.suggestions.length > 0 &&
                onSuggestionClick && (
                  <SuggestionChips
                    suggestions={message.tool_data.suggestions}
                    onSuggestionClick={onSuggestionClick}
                  />
                )}
            </div>
          )}

        {/* Per-response rating — revealed once the answer has finished */}
        {canRate && typewriterDone && onSubmitFeedback && (
          <RatingWidget
            rating={message.rating}
            style={feedbackStyle}
            positiveTags={feedbackTagsPositive}
            negativeTags={feedbackTagsNegative}
            onSubmit={(value, comment, tags) =>
              onSubmitFeedback(message.id, value, comment, tags)
            }
          />
        )}
      </div>
    </div>
  );
};

export default ChatBubble;
